/**
 * Measurement tab for range and bearing between two points.
 * Displays distance and bearing in configurable units.
 */

import { useState } from 'react';
import { Ruler, Send, X, Loader2 } from 'lucide-react';
import { haversineDistance, bearingBetween, formatDistance, formatBearing } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';
import { sendShapeToTak } from './send-shape';

/** Distance unit options for display */
const DISTANCE_UNITS = [
  { value: 0, label: 'Meters' },
  { value: 1, label: 'Kilometers' },
  { value: 2, label: 'Miles' },
  { value: 3, label: 'Nautical Miles' },
] as const;

/** Bearing unit options for display */
const BEARING_UNITS = [
  { value: 0, label: 'Degrees' },
  { value: 1, label: 'Mils' },
] as const;

/** Measurement tab with range and bearing display */
export function MeasurementTab(): React.JSX.Element {
  const { activeTool, drawingState, vertices, startTool, cancelDrawing, setSending, setSent, reset, shapeConfig, updateConfig } = useDrawingStore();
  const [sendError, setSendError] = useState('');

  const isMeasureActive = activeTool === 'measurement';
  const isDrawing = isMeasureActive && drawingState === 'drawing';
  const isConfirming = isMeasureActive && drawingState === 'confirming';
  const isSending = isMeasureActive && drawingState === 'sending';
  const isSent = isMeasureActive && drawingState === 'sent';

  const hasTwoPoints = vertices.length >= 2;
  const distance = hasTwoPoints ? haversineDistance(vertices[0]!, vertices[1]!) : 0;
  const bearing = hasTwoPoints ? bearingBetween(vertices[0]!, vertices[1]!) : 0;

  async function handleSend(): Promise<void> {
    setSending();
    setSendError('');
    const result = await sendShapeToTak('measurement', vertices, 0, shapeConfig);
    setSent(result.ok);
    if (!result.ok) setSendError(result.error);
    if (result.ok) setTimeout(reset, 1500);
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {!isMeasureActive && (
        <button
          onClick={() => startTool('measurement')}
          className="flex items-center justify-center gap-2 rounded bg-echo-yellow px-3 py-2.5 text-sm font-medium text-black"
        >
          <Ruler size={16} /> Measure
        </button>
      )}

      {isDrawing && (
        <div className="space-y-2 rounded bg-white/[0.04] p-3">
          <p className="text-xs text-muted-foreground">
            {vertices.length === 0
              ? 'Click origin point on map.'
              : 'Click destination point.'}
          </p>
          <button onClick={cancelDrawing} className="flex items-center gap-1 rounded bg-white/[0.06] px-3 py-1.5 text-xs text-red-400">
            <X size={12} /> Cancel
          </button>
        </div>
      )}

      {isConfirming && hasTwoPoints && (
        <div className="space-y-3">
          <div className="space-y-2 rounded bg-white/[0.04] p-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Distance</span>
              <span className="font-mono text-foreground">{formatDistance(distance, shapeConfig.rangeUnits)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Bearing</span>
              <span className="font-mono text-foreground">{formatBearing(bearing, shapeConfig.bearingUnits)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Distance Units</label>
              <select
                value={shapeConfig.rangeUnits}
                onChange={(e) => updateConfig({ rangeUnits: Number(e.target.value) })}
                className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground outline-none"
              >
                {DISTANCE_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Bearing Units</label>
              <select
                value={shapeConfig.bearingUnits}
                onChange={(e) => updateConfig({ bearingUnits: Number(e.target.value) })}
                className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground outline-none"
              >
                {BEARING_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={handleSend} className="flex flex-1 items-center justify-center gap-1 rounded bg-echo-yellow px-3 py-2 text-xs font-medium text-black">
              <Send size={12} /> Send to TAK
            </button>
            <button onClick={cancelDrawing} className="rounded bg-white/[0.06] px-3 py-2 text-xs text-red-400">Cancel</button>
          </div>
          {sendError && <p className="text-xs text-red-400">{sendError}</p>}
        </div>
      )}

      {isSending && <p className="text-xs text-muted-foreground"><Loader2 size={14} className="inline animate-spin" /> Sending...</p>}
      {isSent && <p className="text-xs text-green-400">Measurement sent successfully.</p>}
    </div>
  );
}
